import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { trackSkoolClick } from "@/lib/tracking";

const SKOOL_URL = "https://www.skool.com/sircs-reprogramacion-mental-8281";
const HERO_PHOTO =
  "https://customer-assets.emergentagent.com/job_sircs-community/artifacts/6yoqx6pf_093EFD2A-7153-4619-BC41-71EC42F5F297.png";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="surface-dark relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Lighthouse beam — brand signature */}
      <div className="lighthouse-beam" aria-hidden />
      <div className="warm-halo" aria-hidden />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pt-32 pb-20 lg:pt-36 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-10"
            >
              <span className="h-1.5 w-8 bg-warm/80 inline-block" />
              <span className="font-body text-[11px] uppercase tracking-[0.28em] text-pearl/70">
                SIRCS · Reprogramación Cognitiva &amp; Subconsciente
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.12 }}
              data-testid="hero-headline"
              className="font-display text-5xl sm:text-6xl lg:text-[5.5rem] xl:text-[6.5rem] font-normal tracking-[-0.02em] leading-[0.95] text-cream"
            >
              Cambia el código,
              <br />
              <em className="italic text-pearl">rompe el patrón.</em>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.32 }}
              className="mt-8 font-body text-base sm:text-lg text-pearl/80 leading-relaxed max-w-xl"
            >
              Tu mente no es inferior. Está <span className="text-cream">mal programada</span>.
              Deja de repetir los mismos errores y recupera tu poder de decisión dentro de una
              comunidad privada guiada por Santi Lorenzo.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.48 }}
              className="mt-10 flex flex-col sm:flex-row gap-3"
            >
              <a
                href={SKOOL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSkoolClick("hero")}
                data-testid="hero-cta-primary"
                className="group btn-pearl inline-flex items-center justify-between gap-4 px-7 py-4 rounded-full font-body text-[12px] uppercase tracking-[0.22em]"
              >
                Comenzar prueba gratis
                <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
              </a>
              <a
                href="#solucion"
                data-testid="hero-cta-secondary"
                className="btn-outline-pearl inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full font-body text-[12px] uppercase tracking-[0.22em]"
              >
                Ver el sistema
              </a>
            </motion.div>

            {/* Meta strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.7 }}
              className="mt-16 pt-8 border-t border-[#1E252F] grid grid-cols-2 sm:grid-cols-4 gap-6"
            >
              {[
                { k: "Acceso", v: "Privado" },
                { k: "Operador", v: "Santi Lorenzo" },
                { k: "Inicio", v: "Inmediato" },
                { k: "Precio", v: "$47/mes · $299/año" },
              ].map((m, i) => (
                <div key={i} data-testid={`hero-meta-${i}`}>
                  <div className="font-body text-[10px] uppercase tracking-[0.28em] text-silver">
                    {m.k}
                  </div>
                  <div className="mt-1 font-body text-sm text-cream">{m.v}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.0, delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-[8px] border border-[#1E252F] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)]">
              <img
                src={HERO_PHOTO}
                alt="Santi Lorenzo"
                className="w-full h-full object-cover"
                data-testid="hero-photo"
              />
              {/* Soft warm glow from top-right */}
              <div
                className="absolute inset-0 pointer-events-none mix-blend-soft-light"
                style={{
                  background:
                    "radial-gradient(60% 50% at 75% 20%, rgba(201, 168, 106, 0.45), transparent 65%)",
                }}
              />
              {/* Vignette */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(120% 90% at 50% 40%, transparent 50%, rgba(20, 17, 13, 0.45) 100%)",
                }}
              />
              {/* Caption */}
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <div className="font-body text-[10px] uppercase tracking-[0.28em] text-cream/60">
                    Operador
                  </div>
                  <div className="font-display text-2xl text-cream leading-none mt-1">
                    Santi Lorenzo
                  </div>
                </div>
                <div className="font-body text-[10px] uppercase tracking-[0.28em] text-warm">
                  ● en activo
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
